import React, {useEffect, useState} from 'react';
import GenericDialog from "@common/GenericDialog";
import {Divider, Tag, Table, Alert, Badge, Collapse, DatePicker} from "antd";
import TableAntD from "@common/TableAntD";
import axios from "@common/axios";
import dayjs from "dayjs";
import CircularLoader from "@common/CircularLoader";
import Button from "@common/Button";
import {CalendarMonthRounded, ChevronRightRounded, FilterListRounded} from "@mui/icons-material";

const { RangePicker } = DatePicker;

// The Quick Report is on hold for now.

const StudentQuickReportDialog = ({ selectedStudent, isDialogOpen, onClose, className }) => {
    const [loading, setLoading] = useState(false);
    const [studentReport, setStudentReport] = useState();
    const [dateRange, setDateRange] = useState(() => [dayjs().subtract(10, 'week'), dayjs()]);
    const [averageMark, setAverageMark] = useState(0);
    const [isRangePickerOpen, setIsRangePickerOpen] = useState(false);

    const studentName = selectedStudent?.student;
    const studentId = selectedStudent?.studentId;

    const loadReportData = (studentId, dateRange) => {
        setLoading(true);
        const [start, end] = dateRange || [];

        const formattedStart = start ? start.format('DD/MM/YYYY') : undefined;
        const formattedEnd = end ? end.format('DD/MM/YYYY') : undefined;

        axios.get('/web/ui/report/teacher/activity/studentSummary', {
            params: {
                studentId: studentId,
                ...(formattedStart && { startSearch: formattedStart }),
                ...(formattedEnd && { endSearch: formattedEnd }),
                ...((formattedStart && formattedEnd) && { searchPeriod: 'DATE_RANGE' })
            }
        })
            .then(response => {
                if (response.data) {
                    if(response.data.reportData.length > 0){
                        setStudentReport(response.data?.reportData[0]);
                        console.log(response.data?.reportData)
                        // console.log('report data', response.data?.reportData[0])
                    }
                } else {
                    console.error('Failed to load report: No data received');
                }
            })
            .catch(error => {
                console.error('Error fetching report:', error);
            })
            .finally(() => {
                setLoading(false)
            });
    };

    useEffect(() => {
        if(studentId && dateRange && isDialogOpen){
            loadReportData(studentId, dateRange);
        }
    },[studentId, dateRange, isDialogOpen])

    useEffect(() => {
        if (!isDialogOpen) {
            setStudentReport(undefined);
            setAverageMark(0);
            setIsRangePickerOpen(false);
            // Reset date range to default
            setDateRange([dayjs().subtract(10, 'week'), dayjs()]);
        }
    }, [isDialogOpen]);

    // Calculate average mark percentage whenever studentReport changes
    useEffect(() => {
        if (studentReport?.testDetailsParsed?.length > 0) {
            const totalPercentage = studentReport.testDetailsParsed.reduce((sum, test) => {
                const percentage = (test.score / test.totalScore) * 100;
                return sum + percentage;
            }, 0);
            const average = totalPercentage / studentReport.testDetailsParsed.length;
            setAverageMark(Math.round(average));
        } else {
            setAverageMark(0);
        }
    }, [studentReport]);

    const handleDateRangeChange = (dates) => {
        setDateRange(dates);
        // Don't close the collapse automatically - let user close it manually
    };

    const reverseName = (name) => {
        if(!name) return;
        if (name.includes(',')) {
            const [last, first] = name.split(',');
            return `${first.trim()} ${last.trim()}`;
        }
        return name;
    }

    const data = studentReport?.testDetailsParsed?.map((test, index) => ({
        key: index + 1,
        test: test.name,
        result: `${test.score} / ${test.totalScore}`
    })) || [];

    const columns = [
        {
            title: 'Activity Name',
            dataIndex: 'test',
            key: 'test',
        },
        {
            title: 'Result',
            dataIndex: 'result',
            key: 'result',
        },
    ];

    const rangeCollapseItems =[
        {
            key: '1',
            label: 'Range',
            children: (
                <div id="range-content" role="region" aria-labelledby="range-button">
                    <Divider style={{margin: '8px 0'}}/>
                    <RangePicker
                        value={dateRange}
                        onChange={handleDateRangeChange}
                        format="DD/MM/YYYY"
                        style={{ width: '100%' }}
                        allowClear={false}
                    />
                </div>
            )
        }
    ]

    return (
        <GenericDialog
            title={'Quick Report'}
            open={isDialogOpen}
            dialogWidth={500}
            onClose={onClose}
            buttons={[
                {
                    name: 'Close',
                    extraProps: {type: 'text'},
                }
            ]}
        >
            {loading &&
                <CircularLoader />
            }
            {!loading &&
                <>
                    <div className={'ch-class-quick-report-title'}>
                        <div>
                            <h2 className={'ch-class-quick-report-name'}>{reverseName(studentName)}</h2>
                            <p>{studentReport?.studentEmail}</p>
                        </div>
                        <div>
                            <Button
                                id={'range-button'}
                                aria-controls={'range-content'}
                                aria-expanded={isRangePickerOpen}
                                type={'outline'}
                                onClick={() => setIsRangePickerOpen(prev => !prev)}
                            >
                                <span className={'ch-filter-form-button-content'}>
                                    <CalendarMonthRounded />
                                    <ChevronRightRounded
                                        style={{
                                            transform: isRangePickerOpen ? 'rotate(270deg)' : 'rotate(90deg)',
                                            transition:'var(--ani-mid) ease'}}
                                    />
                                </span>
                            </Button>
                        </div>
                    </div>
                    <Tag color={'geekblue'}>{className}</Tag>
                    <Collapse
                        activeKey={isRangePickerOpen ? ['1'] : []}
                        size={"small"}
                        className={'ch-table-filter-collapse-nostyle'}
                        accordion
                        ghost
                        items={rangeCollapseItems}
                    />
                    <Divider style={{margin: '8px 0'}} />
                    <Alert message="The quick report is still a work in progress. Data may be incorrect or incomplete." type="info" showIcon />
                    <Divider style={{margin: '8px 0'}} />
                    <h2 className={'ch-class-quick-report-heading'}>Range averages</h2>
                    <div className={'ch-class-quick-report-stat-wrapper'}>
                        <div className={'ch-class-quick-report-stat'}><span>{averageMark}%</span> Avg. Mark</div>
                        <div className={'ch-class-quick-report-stat'}><span>{studentReport?.countTests}</span> Activities Completed</div>
                    </div>
                    <h2 className={'ch-class-quick-report-heading'}>Range results</h2>
                    <TableAntD
                        columns={columns}
                        dataSource={data}
                        pagination={false}
                        size="small"
                        // rowClassName={(_, index) => (index % 2 === 0 ? 'table-row-light' : 'table-row-dark')}
                    />
                </>
            }
        </GenericDialog>
    );
};

export default StudentQuickReportDialog;